'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * z-index tiers so stacked drawers (e.g. method-editor over integration detail)
 * render above the first-level drawer.
 */
const Z_TIERS = {
    default: { overlay: 'z-[200]', content: 'z-[201]' },
    stacked: { overlay: 'z-[300]', content: 'z-[301]' },
    top: { overlay: 'z-[400]', content: 'z-[401]' },
} as const;

export type SlideDrawerZTier = keyof typeof Z_TIERS;

export interface SlideDrawerProps {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close (backdrop click / Escape) */
    onClose: () => void;
    /** Drawer body */
    children: React.ReactNode;
    /**
     * Tailwind width class for the panel.
     * @default 'w-[60%]'
     */
    width?: string;
    /** Extra className merged onto the panel element */
    className?: string;
    /**
     * z-index tier — use `'stacked'` or `'top'` when opening a drawer
     * on top of another drawer.
     * @default 'default'
     */
    zTier?: SlideDrawerZTier;
}

export function SlideDrawer({
    isOpen,
    onClose,
    children,
    width = 'w-[60%]',
    className,
    zTier = 'default',
}: SlideDrawerProps) {
    const z = Z_TIERS[zTier];

    React.useEffect(() => {
        if (!isOpen) return;
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className={cn('fixed inset-0', z.overlay)} style={{ pointerEvents: 'auto' }}>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className={cn('fixed inset-0 bg-black/50 backdrop-blur-sm', z.overlay)}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onClose();
                        }}
                    />

                    {/* Panel — slides in from the right */}
                    <motion.div
                        initial={{ x: '100%', opacity: 0.5 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: '100%', opacity: 0 }}
                        transition={{ type: 'spring', damping: 28, stiffness: 350 }}
                        className={cn(
                            'fixed right-0 top-0 bottom-0',
                            width,
                            'flex h-full flex-col overflow-hidden',
                            'border-l border-slate-200 bg-white shadow-2xl',
                            'outline-none focus:outline-none',
                            z.content,
                            className,
                        )}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {children}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body,
    );
}
